import type { IFeatureMap } from '../features';
import type { EPackages as PackageName } from '../types';
// License names
import {
  advanced,
  advancedPro,
  base,
  enterprise,
  enterprisePro,
  EPackages,
  essential,
  essentialPro,
  internal,
  lite,
  starter,
} from '../types';
import { Internal } from './internal';
// Feature sets
import {
  EnterpriseFalcon,
  EnterpriseFlamingo,
  Falcon,
  Finch,
  FinchLegacy,
  Phoenix,
} from './legacy';
import {
  Advanced,
  AdvancedPro,
  Base,
  Enterprise,
  EnterprisePro,
  Essential,
  EssentialPro,
  Lite,
  Starter,
} from './platform';

// Type for only the legacy packages
export type TLegacyPackages =
  | EPackages.Finch
  | EPackages.FinchLicense
  | EPackages.FinchLicenseExtended
  | EPackages.FinchLegacy
  | EPackages.FinchLegacyLicense
  | EPackages.Falcon
  | EPackages.FalconLicense
  | EPackages.FalconLicenseExtended
  | EPackages.Phoenix
  | EPackages.PhoenixLicense
  | EPackages.PhoenixLicenseExtended
  | EPackages.EnterpriseFalcon
  | EPackages.EnterpriseFalconSeat
  | EPackages.EnterpriseFalconUnlimitedSeats
  | EPackages.EnterpriseFlamingo
  | EPackages.EnterpriseMarketingSeat
  | EPackages.EnterpriseUnlimitedMarketingSeats
  | EPackages.EnterpriseMonetizationSeat
  | EPackages.EnterpriseUnlimitedMonetizationSeats;

export const legacyPackageHierarchy: TLegacyPackages[] = [
  EPackages.Finch,
  EPackages.FinchLicense,
  EPackages.FinchLicenseExtended,
  EPackages.FinchLegacy,
  EPackages.FinchLegacyLicense,
  EPackages.Falcon,
  EPackages.FalconLicense,
  EPackages.FalconLicenseExtended,
  EPackages.Phoenix,
  EPackages.PhoenixLicense,
  EPackages.PhoenixLicenseExtended,
  EPackages.EnterpriseFalcon,
  EPackages.EnterpriseFalconSeat,
  EPackages.EnterpriseFalconUnlimitedSeats,
  EPackages.EnterpriseFlamingo,
  EPackages.EnterpriseMarketingSeat,
  EPackages.EnterpriseUnlimitedMarketingSeats,
  EPackages.EnterpriseMonetizationSeat,
  EPackages.EnterpriseUnlimitedMonetizationSeats,
];

/**
 * @deprecated Legacy feature map - these packages are no longer sold and will not be supported past 2024 Q1.
 */
const legacyFeatureMap: Record<TLegacyPackages, IFeatureMap> = {
  [EPackages.Finch]: Finch,
  [EPackages.FinchLicense]: Finch,
  [EPackages.FinchLicenseExtended]: Finch,
  [EPackages.FinchLegacy]: FinchLegacy,
  [EPackages.FinchLegacyLicense]: FinchLegacy,
  [EPackages.Falcon]: Falcon,
  [EPackages.FalconLicense]: Falcon,
  [EPackages.FalconLicenseExtended]: Falcon,
  [EPackages.Phoenix]: Phoenix,
  [EPackages.PhoenixLicense]: Phoenix,
  [EPackages.PhoenixLicenseExtended]: Phoenix,
  [EPackages.EnterpriseFalcon]: EnterpriseFalcon,
  [EPackages.EnterpriseFalconSeat]: EnterpriseFalcon,
  [EPackages.EnterpriseFalconUnlimitedSeats]: EnterpriseFalcon,
  [EPackages.EnterpriseFlamingo]: EnterpriseFlamingo,
  [EPackages.EnterpriseMarketingSeat]: EnterpriseFalcon,
  [EPackages.EnterpriseUnlimitedMarketingSeats]: EnterpriseFlamingo,
  [EPackages.EnterpriseMonetizationSeat]: EnterpriseFalcon,
  [EPackages.EnterpriseUnlimitedMonetizationSeats]: EnterpriseFlamingo,
};

export function getLegacyFeatureMap(packageName: TLegacyPackages): IFeatureMap {
  return legacyFeatureMap[packageName];
}

export function isLegacyPackage(packageName: PackageName): packageName is TLegacyPackages {
  return legacyPackageHierarchy.includes(packageName as TLegacyPackages);
}

export function getFeatureMap(packageName: PackageName): IFeatureMap {
  const map: Record<EPackages, IFeatureMap> = {
    // Modern platform packages
    [lite]: Lite,
    [base]: Base,
    [starter]: Starter,
    [essential]: Essential,
    [essentialPro]: EssentialPro,
    [advanced]: Advanced,
    [advancedPro]: AdvancedPro,
    [enterprise]: Enterprise,
    [enterprisePro]: EnterprisePro,
    [internal]: Internal,

    // Legacy packages (deprecated)
    ...legacyFeatureMap,
  };

  return map[packageName];
}
